import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import Loader from 'components/common/Loader';
import Modal from 'components/common/Modal';
import { Button, FormModal, Input } from 'components/common/FormItems';

import {
  createPostboxCredential,
  deletePostboxCredential,
  listPostboxCredentials,
  updatePostboxCredential,
} from './services/documentServices';


const PostboxFormModal = styled(FormModal)`
  div.body {
    max-width: 760px;
  }

  .card-body {
    padding: 1.25rem;
  }

  @media (max-width: 650px) {
    padding: 12px;

    div.body {
      width: 100%;
      padding: 14px;
      margin-top: 2%;
    }

    .card-body {
      padding: 0.75rem;
    }
  }
`;

const Intro = styled.p`
  margin: 0 0 20px;
  color: var(--admincat-color-grey-3);
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;

  h4 {
    margin: 0;
    color: var(--admincat-color-grey-3);
    font-size: 16px;
    font-weight: 600;
    text-align: left;
  }
`;

const LoaderArea = styled.div`
  display: flex;
  min-height: 80px;
  align-items: center;
  justify-content: center;
`;

const CredentialList = styled.div`
  display: grid;
  gap: 10px;
`;

const CredentialCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border: 1px solid var(--admincat-color-grey-6);
  border-radius: 5px;
  background: var(--admincat-color-grey-1);

  @media (max-width: 650px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

const CredentialDetails = styled.div`
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    overflow: hidden;
    color: var(--admincat-color-grey-2);
    font-size: 14px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a {
    display: block;
    margin-top: 3px;
    overflow: hidden;
    color: #4c81be;
    font-size: 12px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ExtractionTime = styled.span`
  display: block;
  margin-top: 5px;
  color: var(--admincat-color-grey-3);
  font-size: 12px;
`;

const InlineActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 6px;

  @media (max-width: 650px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const EmptyState = styled.div`
  padding: 12px;
  border-radius: 5px;
  background: var(--admincat-color-grey-6);

  strong {
    display: block;
    margin-bottom: 4px;
    color: var(--admincat-color-grey-2);
    font-size: 14px;
  }

  p {
    margin: 0;
    color: var(--admincat-color-grey-3);
    font-size: 13px;
    line-height: 1.5;
    text-align: left;
  }
`;

const SectionDivider = styled.div`
  margin: 20px 0;
  border-top: 1px solid var(--admincat-color-grey-6);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;

  > div {
    width: 100%;
    margin-bottom: 0;
  }

  .postbox-full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;

    > div,
    .postbox-full-width {
      grid-column: 1;
    }
  }
`;

const FormHint = styled.p`
  margin: 0 0 12px;
  color: var(--admincat-color-grey-3);
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding-top: 14px;
  border-top: 1px solid var(--admincat-color-grey-6);
`;

const PrimaryButton = styled(Button)`
  min-width: 8.0625rem;
  height: 2.5rem;
  border: 1px solid #1d579b;
  border-radius: 7px;
  background: #4c81be;
  color: #fff;
  font-weight: 500;
  font-size: 1rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SecondaryButton = styled(Button)`
  min-width: 8.0625rem;
  height: 2.5rem;
  border: 1px solid #1d579b;
  border-radius: 7px;
  background: #e4f1ff;
  color: #4c81be;
  font-weight: 500;
  font-size: 1rem;
`;

const SmallButton = styled(Button)`
  height: 34px;
  padding: 5px 10px;
  border: 1px solid #9ebce0;
  border-radius: 6px;
  background: #e4f1ff;
  color: #4c81be;
  font-size: 12px;
`;

const DeleteButton = styled(SmallButton)`
  border-color: #f2c4c4;
  background: #fff1f1;
  color: #b53a3a;
`;

const ErrorAlert = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid #e8b4b4;
  border-radius: 6px;
  background: #fff1f1;
  color: #a13a3a;
  font-size: 13px;
`;

const SummaryList = styled.div`
  display: grid;
  gap: 10px;
`;

const SummaryCard = styled.div`
  padding: 12px;
  border: 1px solid var(--admincat-color-grey-6);
  border-radius: 5px;
  background: var(--admincat-color-grey-1);

  strong {
    color: var(--admincat-color-grey-2);
    font-size: 14px;
  }
`;

const SummaryStatus = styled.div`
  margin-top: 5px;
  color: ${({ reached }) => (reached ? '#39794d' : '#a13a3a')};
  font-size: 12px;
  font-weight: 600;
`;

const SummaryMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: 9px;
  color: var(--admincat-color-grey-3);
  font-size: 12px;
`;

const SummaryError = styled.div`
  margin-top: 8px;
  color: #a13a3a;
  font-size: 12px;
`;

const emptyForm = { description: '', url: '', login: '', password: '' };

const PostboxConfiguration = ({ open, onClose, tr }) => {
  const [credentials, setCredentials] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [error, setError] = useState('');

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      setCredentials(await listPostboxCredentials());
      setError('');
    } catch (requestError) {
      setError(tr('Could not load postbox configurations.'));
    } finally {
      setLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    if (open) loadCredentials();
  }, [open, loadCredentials]);

  if (!open) return null;

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const close = () => {
    resetForm();
    setError('');
    onClose();
  };

  const edit = (credential) => {
    setEditingId(credential.id);
    setForm({
      description: credential.description,
      url: credential.url,
      login: credential.login,
      password: '',
    });
  };

  const updateForm = ({ target }) => {
    setForm((currentForm) => ({
      ...currentForm,
      [target.name]: target.value,
    }));
  };

  const save = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        const updatePayload = { ...form };
        if (!updatePayload.password) delete updatePayload.password;
        await updatePostboxCredential(editingId, updatePayload);
      } else {
        await createPostboxCredential(form);
      }
      resetForm();
      await loadCredentials();
    } catch (requestError) {
      setError(tr('Could not save the postbox configuration.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!credentialToDelete) return;
    setDeleting(true);
    setError('');
    try {
      await deletePostboxCredential(credentialToDelete.id);
      if (editingId === credentialToDelete.id) resetForm();
      setCredentialToDelete(null);
      await loadCredentials();
    } catch (requestError) {
      setError(tr('Could not delete the postbox configuration.'));
    } finally {
      setDeleting(false);
    }
  };

  const formComplete = Boolean(
    form.description.trim()
    && form.url.trim()
    && form.login.trim()
    && (editingId || form.password),
  );

  return (
    <>
      <PostboxFormModal
        open={open}
        title={tr('Configure Postbox extractions')}
        toggleForm={close}
        onSubmit={save}
        formData={form}
        renderActionButtons={() => (
          <Actions className="card-footer text-right">
            <SecondaryButton
              type="button"
              name="Cancel"
              onClick={editingId ? resetForm : close}
            />
            <PrimaryButton
              type="submit"
              name={editingId ? 'Update' : 'Save'}
              isLoading={saving}
              disabled={saving || !formComplete}
            />
          </Actions>
        )}
      >
        <Intro>{tr('Manage secure connections to your document postboxes.')}</Intro>
        {error && <ErrorAlert>{error}</ErrorAlert>}

        <SectionHeader>
          <h4>{tr('Configured postboxes')} ({credentials.length})</h4>
        </SectionHeader>

        {loading ? (
          <LoaderArea><Loader /></LoaderArea>
        ) : credentials.length === 0 ? (
          <EmptyState>
            <strong>{tr('No postboxes configured yet.')}</strong>
            <p>{tr('Add your first postbox connection using the form below.')}</p>
          </EmptyState>
        ) : (
          <CredentialList>
            {credentials.map((credential) => (
              <CredentialCard key={credential.id}>
                <CredentialDetails>
                  <strong>{credential.description}</strong>
                  <a href={credential.url} target="_blank" rel="noopener noreferrer">
                    {credential.url}
                  </a>
                  <ExtractionTime>
                    {tr('Last successful extraction')}: {' '}
                    {credential.last_extracted_at
                      ? new Date(credential.last_extracted_at).toLocaleString()
                      : tr('Never')}
                  </ExtractionTime>
                </CredentialDetails>
                <InlineActions>
                  <SmallButton
                    type="button"
                    name="Edit"
                    onClick={() => edit(credential)}
                  />
                  <DeleteButton
                    type="button"
                    name="Delete"
                    onClick={() => setCredentialToDelete(credential)}
                  />
                </InlineActions>
              </CredentialCard>
            ))}
          </CredentialList>
        )}

        <SectionDivider />
        <SectionHeader>
          <h4>{tr(editingId ? 'Edit postbox configuration' : 'Add postbox configuration')}</h4>
        </SectionHeader>
        <FormHint>{tr('Credentials are encrypted before they are stored.')}</FormHint>
        <FormGrid>
          <Input
            className="postbox-full-width"
            label="Description"
            name="description"
            required
            value={form.description}
            placeholder="e.g. E.ON customer postbox"
            onChange={updateForm}
          />
          <Input
            className="postbox-full-width"
            label="Postbox URL"
            name="url"
            required
            type="url"
            pattern="https://.*"
            value={form.url}
            placeholder="https://"
            onChange={updateForm}
          />
          <Input
            label="Login"
            name="login"
            required
            value={form.login}
            onChange={updateForm}
          />
          <Input
            label="Password"
            name="password"
            required={!editingId}
            type="password"
            value={form.password}
            tinyInfo={editingId ? tr('Leave blank to keep current password') : ''}
            onChange={updateForm}
          />
        </FormGrid>
      </PostboxFormModal>

      <Modal
        toggleModal={Boolean(credentialToDelete)}
        warning
        title="Delete Postbox"
        message="Delete this postbox configuration?"
        buttonTitle="Delete"
        isSubmitting={deleting}
        handleConfrimDelete={confirmRemove}
        handleToggleModal={() => !deleting && setCredentialToDelete(null)}
      />
    </>
  );
};


export const PostboxExtractionSummary = ({ open, onClose, results, tr }) => {
  if (!open) return null;
  return (
    <PostboxFormModal
      open={open}
      title={tr('Postbox extraction summary')}
      toggleForm={onClose}
      onSubmit={(event) => event && event.preventDefault && event.preventDefault()}
      renderActionButtons={() => (
        <Actions className="card-footer text-right">
          <PrimaryButton type="button" name="Close" onClick={onClose} />
        </Actions>
      )}
    >
      <Intro>{tr('Review the result for each configured postbox.')}</Intro>
      <SummaryList>
        {results.map((result, index) => (
          <SummaryCard reached={result.reached} key={result.credential_id || index}>
            <strong>{result.description || tr('Postbox')}</strong>
            <SummaryStatus reached={result.reached}>
              {result.reached ? tr('Reached successfully') : tr('Could not be reached')}
            </SummaryStatus>
            <SummaryMetrics>
              <span>{tr('Documents found')}: {result.documents_found || 0}</span>
              <span>{tr('New documents transferred')}: {result.documents_transferred || 0}</span>
              <span>{tr('Duplicates skipped')}: {result.duplicates_skipped || 0}</span>
            </SummaryMetrics>
            {result.errors && result.errors.map((message, errorIndex) => (
              <SummaryError key={errorIndex}>{message}</SummaryError>
            ))}
          </SummaryCard>
        ))}
      </SummaryList>
    </PostboxFormModal>
  );
};

export default PostboxConfiguration;
